import { useDailyEvent, useMeetingState } from '@daily-co/daily-react';
import { EVENT_NAMES, useAnalytics } from '@web-app/context/AnalyticsContext';
import { useCallback, useEffect, useRef } from 'react';

interface BaseVideoCallAnalyticsParams {
  roomId: string;
  appointmentId?: number;
  clientId?: number;
  providerId?: number;
  participantType: 'provider' | 'patient';
}

interface BaseVideoCallAnalyticsReturn {
  getEventProperties: () => Record<string, unknown>;
  hasTrackedLeft: React.MutableRefObject<boolean>;
  trackEvent: (eventName: string, properties?: Record<string, unknown>) => void;
}

/**
 * Base hook for video call analytics - shared logic for provider and patient.
 *
 * Handles:
 * - Event property generation
 * - Left meeting tracking (left-meeting event)
 * - Window unload tracking (beforeunload event)
 *
 * This hook should NOT be used directly. Use useProviderVideoCallAnalytics
 * or usePatientVideoCallAnalytics instead.
 *
 * @param roomId - The Daily room ID
 * @param appointmentId - Optional Healthie appointment ID
 * @param clientId - Optional Healthie client ID
 * @param providerId - Optional Healthie provider ID
 * @param participantType - Type of participant ('provider' or 'patient')
 */
export const useBaseVideoCallAnalytics = ({
  roomId,
  appointmentId,
  clientId,
  providerId,
  participantType,
}: BaseVideoCallAnalyticsParams): BaseVideoCallAnalyticsReturn => {
  const { trackEvent } = useAnalytics();
  const meetingState = useMeetingState();
  const hasTrackedLeft = useRef(false);

  // Prepare common event properties
  const getEventProperties = useCallback(
    () => ({
      roomId,
      healthieAppointmentId: appointmentId,
      clientId,
      providerId,
      callExperienceVersion: 2, // V2 experience
    }),
    [roomId, appointmentId, clientId, providerId],
  );

  // Track when LOCAL user leaves the meeting
  // Daily.co docs: https://docs.daily.co/reference/daily-js/events/meeting-state#left-meeting
  useDailyEvent(
    'left-meeting',
    useCallback(() => {
      // Prevent duplicate tracking
      if (hasTrackedLeft.current) return;
      hasTrackedLeft.current = true;

      trackEvent(EVENT_NAMES.PARTICIPANT_LEFT_CALL, {
        ...getEventProperties(),
        participantType,
      });
    }, [participantType, trackEvent, getEventProperties]),
  );

  // Track when user closes tab or navigates away
  // Only track if they're actually in the call (meetingState === 'joined-meeting')
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Don't track if we've already tracked a left event
      if (hasTrackedLeft.current) return;

      // Only track if user is currently in the meeting
      if (meetingState === 'joined-meeting') {
        hasTrackedLeft.current = true;

        // Use sendBeacon if available for more reliable tracking on page unload
        // RudderStack will handle this internally via their SDK
        trackEvent(EVENT_NAMES.PARTICIPANT_LEFT_CALL, {
          ...getEventProperties(),
          participantType,
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [meetingState, participantType, trackEvent, getEventProperties]);

  return {
    getEventProperties,
    hasTrackedLeft,
    trackEvent,
  };
};
